import { useState } from "react";
import { Link, usePage } from '@inertiajs/react';
import { HiOutlineDocumentText } from "react-icons/hi";
import { ClipboardDocumentCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { shortenUUID } from "./tasks.page";

export default function BillingPage({ checkout, transactions, credits, creditTransactions }) {

    const { env: { stage }, auth: { user } } = usePage().props;

    return (
        <div className="flex flex-col items-center justify-center flex-grow px-0 md:px-8 lg:px-0">
            {/* <PageHeader title={"Billing"} /> */}

            <div className="w-full max-w-4xl mt-8 space-y-8">
                {!user?.activation.is_activated && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-800 p-4 rounded mb-4 shadow-sm">
                        <p className="font-semibold">Warning: Your account is not activated.</p>
                        <p className="text-sm">Please request and await account activation.</p>
                    </div>
                )}

                <Credits credits={credits} checkout={checkout} />
                {/* {stage == 'dev' && <CheckoutDebug checkout={checkout} />} */}
                <Transactions transactions={transactions} />
                <CreditTransactions creditTransactions={creditTransactions} />
            </div>
        </div>
    ); 
}

// component for debugging only, that displays the checkout object
function CheckoutDebug({ checkout }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="bg-white shadow rounded p-6">
            <h2
                className="text-md font-semibold cursor-pointer select-none flex items-center gap-2"
                onClick={() => setExpanded(e => !e)}
            >
                <span className="inline-block w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-sm">{expanded ? '▼' : '▲'}</span>
                Checkout Debug Info
            </h2>
            {expanded && (
                <pre className="whitespace-pre-wrap text-sm break-words mt-4 bg-gray-50 p-3 rounded">{JSON.stringify(checkout, null, 2)}</pre>
            )}
        </div>
    );
}

// component that shows the current balance of credits, including a button to purchase more credits
function Credits({ checkout, credits }) {
    return (
        <div className="bg-gradient-to-br from-sky-500 via-blue-600 to-blue-700 shadow-lg rounded-xl p-5 sm:p-6 overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-56 h-56 bg-white/10 rounded-full -mr-28 -mt-28 blur-xl"></div>
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-sky-400/20 rounded-full -ml-12 -mb-12 blur-lg"></div>

            <div className="flex flex-col sm:flex-row items-center justify-between text-white relative z-10 gap-4 sm:gap-6">
                <div className="flex items-center gap-4 sm:gap-6">
                    {/* Prominent circular credit badge (adjusted sizes) */}
                    <div className="relative flex items-center">
                        <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-white/10 backdrop-blur flex flex-col items-center justify-center text-white text-4xl sm:text-5xl font-extrabold shadow-sm ring-2 ring-white/20">
                            <span className="leading-none">{credits ?? 0}</span>
                            <span className="text-lg font-thin opacity-80 -mt-1">credits</span>
                        </div>
                        {/* subtle outer glow */}
                        <div className="absolute -inset-px rounded-full blur-md opacity-30" style={{ background: 'radial-gradient(circle at center, rgba(4, 0, 255, 0.64), transparent 100%)' }} aria-hidden="true"></div>
                    </div>

                    <div>
                        <div className="text-base sm:text-lg font-semibold">Available Balance</div>
                        <p className="text-sm mt-1 opacity-90 max-w-xs sm:max-w-md">
                            Use your credits to run AI models in the cloud. <br/>
                            Credits can not be used for other purposes and cannot be paid out.
                        </p>
                    </div>
                </div>

                <div className="sm:self-center w-full sm:w-auto">
                    <button
                        className="bg-white hover:bg-opacity-75 active:bg-opacity-60 text-blue-700 font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all duration-200 transform flex items-center gap-2 w-full sm:w-auto justify-center text-sm"
                        onClick={() => {
                            if (window.Paddle) {
                                window.Paddle.Checkout.open(checkout);
                            }
                        }}
                    >
                        <span>Purchase Credits</span>
                        <ArrowRightIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

// Helpers
function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function formatCurrency(cents, currency = 'USD') {
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((cents ?? 0) / 100);
    } catch (e) {
        return `${(cents ?? 0) / 100} ${currency}`;
    }
}

// small status pill
function StatusPill({ status }) {
    const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
    const map = {
        paid: 'bg-green-100 text-green-800',
        completed: 'bg-green-100 text-green-800',
        pending: 'bg-yellow-100 text-yellow-800',
        failed: 'bg-red-100 text-red-800',
        refunded: 'bg-gray-100 text-gray-800'
    };
    const style = map[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    return <span className={`${base} ${style}`}>{status}</span>;
}

// component that shows past transactions, including the date, amount, and status and a button to download invoices
function Transactions({ transactions }) {

    return (
        <div className="mb-6">
            {/* floating header */}
            <div className="flex items-center justify-between mb-3">
                <div className="my-5">
                    <h2 className="inline-block text-lg font-semibold">Past Transactions</h2>
                    <p className="text-sm text-gray-500 mt-1">Invoices and purchase history</p>
                </div>
            </div>

            {/* table moved out to its own rounded container with border and light shadow */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {(transactions ?? []).map(tx => (
                                <tr key={tx.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(tx.billed_at)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(tx.total, tx.currency ?? 'USD')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <StatusPill status={tx.status} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {tx.invoice_number ? (
                                            <a
                                                href={`/billing/${tx.paddle_id}/invoice`}
                                                className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-sky-50 text-sky-700 hover:bg-sky-100 text-sm"
                                            >
                                                <HiOutlineDocumentText className="h-4 w-4" />
                                                {tx.invoice_number}
                                            </a>
                                        ) : (
                                            <span className="text-sm text-gray-500">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {(!transactions || transactions.length === 0) && (
                                <tr>
                                    <td colSpan={4} className="py-8 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2">
                                            <svg className="h-8 w-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M3 11h18M3 15h18M3 19h18"></path></svg>
                                            <div className="text-sm">No transactions found.</div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function CreditsPill({ credits, showSign = true }) {

    // color (green for positive, red for negative)
    const color = credits > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    const sign = credits > 0 ? '+' : '';

    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
            <span>
                {showSign && sign}{credits} 
            </span>
            <span className="ml-1">
                credit{credits**2 === 1 ? '' : 's'}
            </span>
        </span>
    );
}

function CreditTransactions({ creditTransactions }) {

    return (
        <div className="mt-6 mb-6">
            {/* floating header */}
            <div className="flex items-center justify-between mb-3">
                <div className="my-5">
                    <h2 className="inline-block text-lg font-semibold">Credit Transactions</h2>
                    <p className="text-sm text-gray-500 mt-1">History of credits earned or spent</p>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {(creditTransactions ?? []).map(tx => (
                                <tr key={tx.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(tx.created_at)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        <CreditsPill credits={tx.credits} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                        {(tx.transactionable_type === 'App\\Models\\Task') &&
                                            <Link href={`/tasks?taskId=${tx.transactionable_id}`} className="text-sky-600 hover:underline">
                                                <ClipboardDocumentCheckIcon className="h-4 w-4 inline-block mr-1 text-gray-400" />
                                                Task #{shortenUUID(tx.transactionable.uuid)}
                                            </Link>
                                        }
                                        {(tx.transactionable_type === 'Laravel\\Paddle\\Transaction') &&
                                            <span className="inline-flex items-center gap-2">
                                                <HiOutlineDocumentText className="h-4 w-4 text-gray-400" />
                                                Transaction {tx.transactionable?.invoice_number}
                                            </span>
                                        }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{tx.description}</td>
                                </tr>
                            ))}

                            {(!creditTransactions || creditTransactions.length === 0) && (
                                <tr>
                                    <td colSpan={3} className="py-8 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2">
                                            <svg className="h-8 w-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c1.657 0 3-1.567 3-3.5S13.657 1 12 1 9 2.567 9 4.5 10.343 8 12 8zM21 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2"></path></svg>
                                            <div className="text-sm">No credit transactions found.</div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}